import { FC, useState, useCallback, useMemo } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { VoxProductCatalogItem, VoxRoiCostConfigData, CostScheduleEntry, BeforeProviderEntry } from '../../models/voxRoi';
import { validateSchedule } from './voxRoiCalc';
import { ProviderMark } from './ProviderMarks';
import styles from './VoxRoiCostRail.module.css';

export interface VoxRoiCostRailProps {
  config: VoxRoiCostConfigData;
  catalog: VoxProductCatalogItem[];
  onSave: (config: VoxRoiCostConfigData) => Promise<void>;
  isSaving: boolean;
  saveError: string | null;
}

interface VendorGroup {
  vendor: string;
  items: VoxProductCatalogItem[];
}

const toIsoDateString = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const groupCatalogByVendor = (catalog: VoxProductCatalogItem[]): VendorGroup[] => {
  const vendorMap = new Map<string, VoxProductCatalogItem[]>();
  for (const item of catalog) {
    const existing = vendorMap.get(item.vendor);
    if (existing) {
      existing.push(item);
    } else {
      vendorMap.set(item.vendor, [item]);
    }
  }
  return Array.from(vendorMap.entries()).map(([vendor, items]) => ({ vendor, items }));
};

const VoxRoiCostRail: FC<VoxRoiCostRailProps> = ({ config, catalog, onSave, isSaving, saveError }) => {
  const [costs, setCosts] = useState<Record<string, CostScheduleEntry[]>>(() => {
    const initial: Record<string, CostScheduleEntry[]> = {};
    for (const item of catalog) {
      initial[item.productKey] = config.costs[item.productKey] ?? [];
    }
    return initial;
  });
  const [beforeProviders, setBeforeProviders] = useState<Record<string, BeforeProviderEntry>>(
    () => ({ ...config.beforeProviders }),
  );
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [collapsedVendors, setCollapsedVendors] = useState<Set<string>>(() => new Set());
  const [validationError, setValidationError] = useState<string | null>(null);

  const vendorGroups = useMemo(() => groupCatalogByVendor(catalog), [catalog]);

  const isDirty = useMemo(() => {
    if (JSON.stringify(beforeProviders) !== JSON.stringify(config.beforeProviders)) return true;
    return JSON.stringify(costs) !== JSON.stringify(config.costs);
  }, [costs, beforeProviders, config]);

  const handleBeforeToggle = useCallback((productKey: string) => {
    setBeforeProviders((previous) => {
      if (productKey in previous) {
        const { [productKey]: _, ...rest } = previous;
        return rest;
      }
      return { ...previous, [productKey]: { ordersPerLoan: 0.9, cost: 0 } };
    });
  }, []);

  const handleBeforeProviderChange = useCallback(
    ({ productKey, updates }: { productKey: string; updates: Partial<BeforeProviderEntry> }) => {
      setBeforeProviders((previous) => ({
        ...previous,
        [productKey]: { ...previous[productKey], ...updates },
      }));
    },
    [],
  );

  const handleToggleVendor = useCallback((vendor: string) => {
    setCollapsedVendors((previous) => {
      const next = new Set(previous);
      if (next.has(vendor)) {
        next.delete(vendor);
      } else {
        next.add(vendor);
      }
      return next;
    });
  }, []);

  const handleAddEntry = useCallback((productKey: string) => {
    setCosts((previous) => {
      const existing = previous[productKey] ?? [];
      const newEntry: CostScheduleEntry = {
        effectiveFrom: toIsoDateString(new Date()),
        effectiveTo: null,
        cost: 0,
      };
      return { ...previous, [productKey]: [...existing, newEntry] };
    });
  }, []);

  const handleUpdateEntry = useCallback(({ productKey, index, updates }: { productKey: string; index: number; updates: Partial<CostScheduleEntry> }) => {
    setCosts((previous) => {
      const entries = [...(previous[productKey] ?? [])];
      entries[index] = { ...entries[index], ...updates };
      return { ...previous, [productKey]: entries };
    });
  }, []);

  const handleRemoveEntry = useCallback(({ productKey, index }: { productKey: string; index: number }) => {
    setCosts((previous) => {
      const entries = (previous[productKey] ?? []).filter((_, entryIndex) => entryIndex !== index);
      return { ...previous, [productKey]: entries };
    });
  }, []);

  const handleSave = useCallback(async () => {
    for (const [productKey, schedule] of Object.entries(costs)) {
      if (schedule.length > 0) {
        const scheduleError = validateSchedule(schedule);
        if (scheduleError) {
          setValidationError(`${productKey}: ${scheduleError}`);
          return;
        }
      }
    }
    setValidationError(null);
    await onSave({
      version: config.version,
      costs,
      beforeProviders,
    });
  }, [onSave, config.version, costs, beforeProviders]);

  const handleCancel = useCallback(() => {
    setCosts(() => {
      const initial: Record<string, CostScheduleEntry[]> = {};
      for (const item of catalog) {
        initial[item.productKey] = config.costs[item.productKey] ?? [];
      }
      return initial;
    });
    setBeforeProviders({ ...config.beforeProviders });
    setValidationError(null);
  }, [config, catalog]);

  const beforeProviderKeys = Object.keys(beforeProviders);

  return (
    <div className={styles.rail}>
      {/* Card 1: Lender Inputs */}
      <div className={styles.railCard}>
        <div className={styles.railCardHeader}>
          <i className="pi pi-sliders-h" />
          <span className={styles.railCardTitle}>Lender Inputs</span>
        </div>
        <p className={styles.railCardDescription}>
          Select the providers you used before VoX and enter each provider&rsquo;s orders per loan and cost per order.
        </p>

        <div className={styles.beforeProviderList}>
          {catalog.map((item) => {
            const isBefore = item.productKey in beforeProviders;
            return (
              <div key={item.productKey} className={styles.beforeProviderItem}>
                <div className={styles.beforeProviderRow}>
                  <div className={styles.beforeCheck}>
                    <Checkbox
                      checked={isBefore}
                      onChange={() => handleBeforeToggle(item.productKey)}
                      aria-label={`Before ${item.productDisplayName}`}
                    />
                  </div>
                  <ProviderMark vendor={item.vendor} />
                  <div className={styles.beforeProviderInfo}>
                    <span className={styles.beforeProviderName}>{item.productDisplayName}</span>
                    <span className={styles.beforeProviderVendor}>{item.vendor}</span>
                  </div>
                </div>
                {isBefore && (
                  <div className={styles.beforeProviderFields}>
                    <div className={styles.entryField}>
                      <label className={styles.entryLabel}>Orders / Loan</label>
                      <InputNumber
                        value={beforeProviders[item.productKey].ordersPerLoan}
                        onValueChange={(event) => handleBeforeProviderChange({ productKey: item.productKey, updates: { ordersPerLoan: event.value ?? 0.1 } })}
                        minFractionDigits={1}
                        maxFractionDigits={2}
                        min={0.1}
                        max={99}
                        inputClassName={styles.smallInput}
                      />
                    </div>
                    <div className={styles.entryField}>
                      <label className={styles.entryLabel}>Cost ($)</label>
                      <InputNumber
                        value={beforeProviders[item.productKey].cost}
                        onValueChange={(event) => handleBeforeProviderChange({ productKey: item.productKey, updates: { cost: event.value ?? 0 } })}
                        mode="currency"
                        currency="USD"
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        min={0}
                        inputClassName={styles.costInput}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {beforeProviderKeys.length === 0 && (
          <div className={styles.noBeforeHint}>
            Check at least one provider to model your Before spend.
          </div>
        )}
      </div>

      {/* Card 2: Provider & Product Costs */}
      <div className={styles.railCard}>
        <div className={styles.railCardHeader}>
          <i className="pi pi-dollar" />
          <span className={styles.railCardTitle}>Provider &amp; Product Costs</span>
        </div>
        <p className={styles.railCardDescription}>
          Date-effective cost schedule for each product. These costs price your After (VoX) spend.
        </p>

        <div className={styles.vendorGroupList}>
          {vendorGroups.map((vendorGroup) => {
            const isCollapsed = collapsedVendors.has(vendorGroup.vendor);
            return (
              <div key={vendorGroup.vendor} className={styles.vendorGroup}>
                <button
                  type="button"
                  className={styles.vendorGroupToggle}
                  onClick={() => handleToggleVendor(vendorGroup.vendor)}
                  aria-expanded={!isCollapsed}
                >
                  <ProviderMark vendor={vendorGroup.vendor} />
                  <span className={styles.vendorGroupName}>{vendorGroup.vendor}</span>
                  <span className={styles.vendorGroupCount}>{vendorGroup.items.length}</span>
                  <i className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-down'} ${styles.chevron}`} />
                </button>

                {!isCollapsed && (
                  <div className={styles.vendorGroupProducts}>
                    {vendorGroup.items.map((item) => {
                      const schedule = costs[item.productKey] ?? [];
                      const isExpanded = expandedProduct === item.productKey;
                      const latestCost = schedule.length > 0
                        ? schedule[schedule.length - 1].cost
                        : undefined;

                      return (
                        <div key={item.productKey} className={styles.productItem}>
                          <button
                            type="button"
                            className={styles.productToggle}
                            onClick={() => setExpandedProduct(isExpanded ? null : item.productKey)}
                            aria-expanded={isExpanded}
                          >
                            <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} ${styles.chevron}`} />
                            <span className={styles.productName}>{item.productDisplayName}</span>
                            <span className={styles.productCostBadge}>
                              {latestCost !== undefined ? `$${latestCost.toFixed(2)}` : '---'}
                            </span>
                          </button>

                          {isExpanded && (
                            <div className={styles.schedulePanel}>
                              {schedule.length === 0 && (
                                <div className={styles.noEntries}>No cost entries</div>
                              )}
                              {schedule.map((entry, entryIndex) => (
                                <div key={entryIndex} className={styles.scheduleEntry}>
                                  <div className={styles.entryRow}>
                                    <div className={styles.entryField}>
                                      <label className={styles.entryLabel}>From</label>
                                      <div className={styles.datePicker}>
                                        <Calendar
                                          value={new Date(entry.effectiveFrom + 'T00:00:00')}
                                          onChange={(event) => {
                                            if (event.value) handleUpdateEntry({ productKey: item.productKey, index: entryIndex, updates: { effectiveFrom: toIsoDateString(event.value as Date) } });
                                          }}
                                          dateFormat="yy-mm-dd"
                                          showIcon
                                        />
                                      </div>
                                    </div>
                                    <div className={styles.entryField}>
                                      <label className={styles.entryLabel}>To</label>
                                      <div className={styles.datePicker}>
                                        <Calendar
                                          value={entry.effectiveTo ? new Date(entry.effectiveTo + 'T00:00:00') : null}
                                          onChange={(event) => {
                                            handleUpdateEntry({ productKey: item.productKey, index: entryIndex, updates: {
                                              effectiveTo: event.value ? toIsoDateString(event.value as Date) : null,
                                            } });
                                          }}
                                          dateFormat="yy-mm-dd"
                                          showIcon
                                          placeholder="Open-ended"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className={styles.entryRow}>
                                    <div className={styles.entryField}>
                                      <label className={styles.entryLabel}>Cost ($)</label>
                                      <InputNumber
                                        value={entry.cost}
                                        onValueChange={(event) => handleUpdateEntry({ productKey: item.productKey, index: entryIndex, updates: { cost: event.value ?? 0 } })}
                                        mode="currency"
                                        currency="USD"
                                        minFractionDigits={2}
                                        maxFractionDigits={2}
                                        min={0}
                                        inputClassName={styles.costInput}
                                      />
                                    </div>
                                    <div className={styles.removeBtn}>
                                      <Button
                                        icon="pi pi-trash"
                                        severity="danger"
                                        text
                                        size="small"
                                        onClick={() => handleRemoveEntry({ productKey: item.productKey, index: entryIndex })}
                                        aria-label="Remove entry"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <div className={styles.addBtn}>
                                <Button
                                  label="Add period"
                                  icon="pi pi-plus"
                                  text
                                  size="small"
                                  onClick={() => handleAddEntry(item.productKey)}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {validationError && <Message severity="error" text={validationError} />}
      {saveError && <Message severity="error" text={saveError} />}

      {isDirty && (
        <div className={styles.railActions}>
          <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={isSaving} size="small" />
          <Button label="Cancel" icon="pi pi-times" severity="secondary" outlined onClick={handleCancel} disabled={isSaving} size="small" />
        </div>
      )}
    </div>
  );
};

export default VoxRoiCostRail;
